import { useMemo, useState } from "react";
import { useSnackbar } from "./Snackbar";
import { addVariantToCart } from "../lib/cart";
import { discountedMoney, formatMoney } from "../lib/format";
import type { PocaConfig, Product, ProductImage } from "../types";

type Props = {
  product: Product;
  config: PocaConfig;
  onNavigate?: (handle: string) => void;
};

function resolveCardImages(product: Product): {
  primary: ProductImage | null;
  hover: ProductImage | null;
} {
  const primary = product.featuredImage ?? product.images[0] ?? null;
  if (!primary) {
    return { primary: null, hover: null };
  }

  const hover =
    product.images.find((img) => img.url !== primary.url) ??
    product.images[1] ??
    null;

  if (!hover || hover.url === primary.url) {
    return { primary, hover: null };
  }

  return { primary, hover };
}

export function ProductCard({ product, config, onNavigate }: Props) {
  const { showSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { primary: primaryImage, hover: hoverImage } = useMemo(
    () => resolveCardImages(product),
    [product]
  );
  const hasHoverFlip = hoverImage != null;

  const firstVariant = product.variants[0];
  const canBuy =
    config.enableAddToCart && product.availableForSale && !!firstVariant;

  // 스토어 전역 할인(테마 에디터 설정). 켜져 있으면 현재가를 원가처럼 긋고 할인가를 보여준다.
  // compare-at 세일과 겹치면 표시가 이중으로 꼬여서, 전역 할인이 우선하고 compare-at 표기는 접는다.
  const storeDiscount = config.discountPercent > 0;
  const basePrice = product.priceRange.minVariantPrice;
  const price = formatMoney(
    storeDiscount ? discountedMoney(basePrice, config.discountPercent) : basePrice,
    config.locale
  );
  const compareAt = storeDiscount ? basePrice : product.compareAtPriceRange.minVariantPrice;
  const showCompareAt = storeDiscount
    ? true
    : !!(
        compareAt &&
        Number.parseFloat(compareAt.amount) >
          Number.parseFloat(basePrice.amount)
      );

  const savingsPercent = storeDiscount
    ? Math.round(config.discountPercent)
    : showCompareAt && compareAt
      ? Math.round(
          (1 -
            Number.parseFloat(basePrice.amount) /
              Number.parseFloat(compareAt.amount)) *
            100
        )
      : 0;

  const handleAdd = async () => {
    if (!canBuy || busy) return;
    setBusy(true);
    setFeedback(null);
    const result = await addVariantToCart(config, firstVariant.id, 1);
    setBusy(false);
    if (result.ok) {
      setFeedback("Added!");
      window.setTimeout(() => setFeedback(null), 2000);
    } else {
      showSnackbar(result.message ?? "Could not add to cart");
    }
  };

  const imageAlt = primaryImage?.altText ?? product.title;

  return (
    <article className="poca-card">
      <div
        className={`poca-card__media-wrap${hasHoverFlip ? " poca-card__media-wrap--dual" : ""}`}
      >
        <a
          className="poca-card__link"
          href={`/products/${product.handle}`}
          onClick={(e) => {
            if (onNavigate && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
              e.preventDefault();
              onNavigate(product.handle);
            }
          }}
        >
          <div className="poca-card__media">
            {primaryImage ? (
              <div
                className={`poca-card__media-flip${hasHoverFlip ? " poca-card__media-flip--dual" : ""}`}
              >
                <div className="poca-card__media-face poca-card__media-face--front">
                  <img
                    src={primaryImage.url}
                    alt={imageAlt}
                    loading="lazy"
                    width={primaryImage.width}
                    height={primaryImage.height}
                  />
                </div>
                {hoverImage && (
                  <div className="poca-card__media-face poca-card__media-face--back">
                    <img
                      src={hoverImage.url}
                      alt={hoverImage.altText ?? imageAlt}
                      loading="eager"
                      width={hoverImage.width}
                      height={hoverImage.height}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="poca-card__noimage">No image</div>
            )}
            {!product.availableForSale && (
              <span className="poca-card__badge poca-card__badge--sold">
                Sold out
              </span>
            )}
            {showCompareAt && product.availableForSale && savingsPercent > 0 && (
              <span className="poca-card__badge poca-card__badge--sale">
                {savingsPercent}% off
              </span>
            )}
          </div>
        </a>
      </div>
      <a
        className="poca-card__link poca-card__body-link"
        href={`/products/${product.handle}`}
        onClick={(e) => {
          if (onNavigate && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
            e.preventDefault();
            onNavigate(product.handle);
          }
        }}
      >
        <div className="poca-card__body">
          <h3 className="poca-card__title">{product.title}</h3>
          {/* cbb-ignored-price: Currency Converter Plus 앱이 이 가격을 다시 파싱·치환하지 못하게 막는다.
              Storefront API 가 이미 마켓 통화로 준 금액이라 변환이 필요 없고, 앱이 "€4.95" 를
              EUR 표시형식(€4,95)으로 읽어 495 로 오판하는 문제가 있다. */}
          <div className="poca-card__prices cbb-ignored-price">
            <span
              className={`poca-card__price${showCompareAt ? " poca-card__price--sale" : ""}`}
            >
              {price}
            </span>
            {showCompareAt && compareAt && (
              <span className="poca-card__compare">
                {formatMoney(compareAt, config.locale)}
              </span>
            )}
          </div>
        </div>
      </a>
      {config.enableAddToCart && (
        <button
          type="button"
          className="poca-card__cta"
          disabled={!canBuy || busy}
          onClick={handleAdd}
        >
          {!product.availableForSale
            ? "Sold out"
            : busy
              ? "Adding…"
              : (feedback ?? "Add to cart")}
        </button>
      )}
    </article>
  );
}
